import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser, fetchUserProfile, changePassword, getPasswordHistory, getDeletedUsers, restoreDeleteUser } from '../../../services/users';

export const loginUserAsync = createAsyncThunk(
    'userInfo/loginUser',
    async (credentials, { rejectWithValue, dispatch }) => {
        try {
            const response = await loginUser(credentials);

            const userData = response.data?.data?.user ||
                response.data?.user ||
                response.data ||
                null;

            const token = response.data?.data?.token ||
                response.data?.token ||
                localStorage.getItem('token');

            if (token) {
                localStorage.setItem('token', token);
                localStorage.setItem('loginTime', new Date().getTime().toString());
            }

            const profileResponse = await fetchUserProfile();
            dispatch(setProfileData(profileResponse?.data?.data));

            return {
                userdata: userData,
                token: token
            };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Login failed';
            return rejectWithValue(errorMessage);
        }
    }
);

export const changePasswordAsync = createAsyncThunk(
    'auth/changePassword',
    async (passwordData) => {
        const response = await changePassword(passwordData);
        return response;
    }
);

export const fetchPasswordHistoryAsync = createAsyncThunk(
    'auth/getPasswordHistory',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getPasswordHistory();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch balance');
        }
    }
);

export const fetchDeletedUsersAsync = createAsyncThunk(
    'auth/getDeletedUsers',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getDeletedUsers();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch deleted users');
        }
    }
)

export const restoreDeleteUserAsync = createAsyncThunk(
    'auth/restoreDeleteUser',
    async (id, { rejectWithValue }) => {
        try {
            const response = await restoreDeleteUser(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to restore delete user');
        }
    }
)

const userInfoSlice = createSlice({
    name: 'authSlice',
    initialState: {
        userdata: null,
        profileData: null,
        passwordHistory: [],
        deletedUsers: [],
        token: localStorage.getItem('token') || null,
        loading: false,
        error: null,
        isAuthenticated: false
    },
    reducers: {
        setUserInfo(state, action) {
            state.userdata = action.payload;
            state.isAuthenticated = !!action.payload;
        },
        setProfileData(state, action) {
            state.profileData = action.payload;
        },
        emptyUserInfo(state) {
            state.userdata = null;
            state.token = null;
            state.isAuthenticated = false;
            localStorage.removeItem('token');
        },
        logout(state) {
            state.userdata = null;
            state.token = null;
            state.isAuthenticated = false;
            localStorage.removeItem('token');
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUserAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUserAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.userdata = action.payload.userdata;
                state.token = action.payload.token;
                state.isAuthenticated = !!action.payload.userdata;
                state.error = null;
            })
            .addCase(loginUserAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.isAuthenticated = false;
                state.userdata = null;
                state.token = null;
            })

            .addCase(changePasswordAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(changePasswordAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(changePasswordAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchPasswordHistoryAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPasswordHistoryAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.passwordHistory = action.payload;
                state.error = null;
            })
            .addCase(fetchPasswordHistoryAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchDeletedUsersAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDeletedUsersAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.deletedUsers = action.payload;
                state.error = null;
            })
            .addCase(fetchDeletedUsersAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });


    },
});

export const { setUserInfo, setProfileData, emptyUserInfo, logout } = userInfoSlice.actions;

export default userInfoSlice.reducer;